// Apolo Loop Engineering — OBJECTIVE GATE registry. PURE.
//
// THE rule (from loop engineering — "sem gate, o agente se aprova"): completion of
// a step is decided by a deterministic check over OBSERVABLE signals, NEVER by the
// model declaring itself done. Every gate here ignores any `modelSaysDone`/text and
// reads only injected signals (file written? test/green_pass? audit clean?).
//
// A Gate is a pure sync fn: (signals) => { verdict:'pass'|'fail'|'pending', evidence:string[] }.

/** green_pass: trust an explicit boolean signal from a real check (CI/test runner). */
export function greenPass(signals = {}) {
  if (signals.greenPass === true) return { verdict: 'pass', evidence: ['green_pass=true'] };
  if (signals.greenPass === false) return { verdict: 'fail', evidence: ['green_pass=false'] };
  return { verdict: 'pending', evidence: ['green_pass signal absent'] };
}

/** deliverable_audit: pass only when audited AND no ERROR-level findings. */
export function deliverableAudit(signals = {}) {
  const errs = Array.isArray(signals.auditErrors) ? signals.auditErrors : null;
  if (errs && errs.length > 0) {
    return { verdict: 'fail', evidence: errs.slice(0, 10).map((e) => `audit:${e}`) };
  }
  if (signals.audited === true) return { verdict: 'pass', evidence: ['audit clean'] };
  return { verdict: 'pending', evidence: ['not audited yet'] };
}

/** noop: EXPLICIT "no gate" — visible + testable, never an accidental self-approval. */
export function noopGate() {
  return { verdict: 'pass', evidence: ['noop (no objective gate selected)'] };
}

/** ranChecks: did a real check (test/lint/build) run this turn? Pass only when
 * at least one non-error run_command result matches the check pattern. */
export function ranChecks(signals = {}) {
  const cmds = Array.isArray(signals.checkCommands) ? signals.checkCommands : [];
  const ran = cmds.filter((c) => typeof c === 'string' && /\b(test|lint|build|check|vitest|jest|tsc)\b/i.test(c));
  if (ran.length > 0) return { verdict: 'pass', evidence: [`ran: ${ran[0].slice(0, 80)}`] };
  return { verdict: 'pending', evidence: ['no check command ran this turn'] };
}

export const GATES = {
  green_pass: greenPass,
  deliverable_audit: deliverableAudit,
  ran_checks: ranChecks,
  noop: noopGate,
};

/**
 * Resolve a gate by name. Unknown/falsy → noop (explicit), so a typo never silently
 * blocks; but the chosen gate is always one of the registry's pure fns.
 */
export function resolveGate(name) {
  return GATES[name] || noopGate;
}

/**
 * Compose: a step is COMPLETE iff the gate says 'pass'. Anything else (fail/pending)
 * keeps the step open. This is the single chokepoint that makes self-approval
 * structurally impossible — there is no branch that reads model text.
 */
export function isStepComplete(verdict) {
  return verdict?.verdict === 'pass';
}
